import { NgClass } from '@angular/common';
import { Component, effect, input, OnInit, viewChildren } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { Address, FindZoneParams, Zone } from '@domains';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { MapComponent } from '@rspl-map';
import {
  CardComponent,
  ConfirmDialogComponent,
  FxFlexDirective,
  FxLayoutAlignDirective,
  FxLayoutDirective,
  FxLayoutGapDirective,
  Responsive,
  ResponsiveService,
} from '@rspl-ui';
import { take, takeUntil } from 'rxjs';

import { AppState } from '../../../store/app-state.interface';
import { createZoneSuccess, deleteZone, deleteZoneSuccess, getZones, updateZoneSuccess } from '../../../store/zone/zone.actions';
import { selectZones } from '../../../store/zone/zone.selectors';
import { SaveZoneDialogComponent } from '../save-zone-dialog/save-zone-dialog.component';

@Component({
  selector: 'app-zone-list',
  templateUrl: './zone-list.component.html',
  styleUrls: ['./zone-list.component.scss'],
  imports: [
    MatTabGroup,
    MatTab,
    CardComponent,
    FxFlexDirective,
    FxLayoutAlignDirective,
    FxLayoutDirective,
    FxLayoutGapDirective,
    MapComponent,
    MatIconButton,
    MatIcon,
    MatButton,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem,
    NgClass,
  ],
})
export class ZoneListComponent extends Responsive implements OnInit {
  readonly maps = viewChildren<MapComponent>(MapComponent);
  parentType = input.required<'truck' | 'store'>();
  grandParentId = input.required<string>();
  parentId = input.required<string>();
  address = input.required<Address>();
  filter: FindZoneParams;
  zones: Zone[] = [];

  constructor(
    public override responsiveService: ResponsiveService,
    private store: Store<AppState>,
    private actions: Actions,
    private dialog: MatDialog,
  ) {
    super(responsiveService);

    effect(() => {
      if (this.parentId() && this.parentType() && !this.filter) {
        this.selectedIndexChange(0);
      }
    });
    effect(() => {
      this.maps()?.forEach((map) => map.setAddress(this.address(), false));
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.store.pipe(select(selectZones), takeUntil(this.destroy$)).subscribe((zones) => (this.zones = zones));
    this.actions
      .pipe(ofType(deleteZoneSuccess, updateZoneSuccess, createZoneSuccess), takeUntil(this.destroy$))
      .subscribe(() => this.fetchZones());
  }

  selectedIndexChange(index: number): void {
    if (index === 0) {
      this.filter = {
        ...(this.parentType() === 'truck' ? { partner_vehicle: this.parentId() } : { charity_store: this.parentId() }),
        template_type: 'custom',
        order: 'created_at',
        order_direction: 'desc',
      };
    } else {
      this.filter = {
        ...(this.parentType() === 'truck' ? { partner_vehicle: this.parentId() } : { charity_store: this.parentId() }),
        template_type: 'recommended',
        order: 'created_at',
        order_direction: 'desc',
      };
    }

    this.fetchZones();
  }

  fetchZones() {
    this.store.dispatch(getZones({ findParams: this.filter }));
  }

  editZone(zone: Zone) {
    this.dialog.open(SaveZoneDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      data: {
        title: 'Edit Zone',
        parentId: this.parentId(),
        address: this.address(),
        zone: zone,
      },
    });
  }

  createZone(zone: Zone) {
    this.dialog.open(SaveZoneDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      data: {
        title: 'Create Zone',
        parentId: this.parentId(),
        address: this.address(),
        zone: {
          zips: zone.zips,
          partnerId: this.parentType() === 'truck' ? this.grandParentId() : undefined,
          charityId: this.parentType() === 'store' ? this.grandParentId() : undefined,
          vehicle_id: this.parentType() === 'truck' ? this.parentId() : undefined,
          store_id: this.parentType() === 'store' ? this.parentId() : undefined,
          template_type: 'custom',
        },
      },
    });
  }

  createNewZone() {
    this.dialog.open(SaveZoneDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      data: {
        title: 'Create Zone',
        parentId: this.parentId(),
        address: this.address(),
        zone: {
          zips: [],
          partnerId: this.parentType() === 'truck' ? this.grandParentId() : undefined,
          charityId: this.parentType() === 'store' ? this.grandParentId() : undefined,
          vehicle_id: this.parentType() === 'truck' ? this.parentId() : undefined,
          store_id: this.parentType() === 'store' ? this.parentId() : undefined,
          template_type: 'custom',
        },
      },
    });
  }

  deleteZone(zone: Zone) {
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '500px',
        maxWidth: '100vw',
        data: {
          title: 'Delete Zone',
          message: `Are you sure you want to delete ${zone.name}?`,
          yes: 'Delete',
          no: 'Back',
          color: 'warn',
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.store.dispatch(deleteZone({ zoneId: zone.id }));
        }
      });
  }
}
